import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { REDIRECT_VERIFY_EMAIL_KEY } from '@/api';
import { auth } from '@/firebase';
import {
  clearExtensionReturnPath,
  resolvePostLoginPath,
} from '@/extension/extensionReturnPath';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Reads the live Firebase auth `emailVerified` so we don't have to plumb
 * the flag through every `setUser` callsite in `AuthContext`. The backend
 * also enforces this — but blocking at the route level prevents the
 * dashboard from booting a flood of API calls that all 403 with
 * EMAIL_NOT_VERIFIED.
 *
 * Inside the extension (chrome-extension://...) the embedded React app has
 * no Firebase session of its own — the service worker does. `auth.currentUser`
 * is therefore always null and we can't read `emailVerified` directly. In that
 * mode we trust `AuthContext.user` instead: AuthContext only sets a non-null
 * `user` after `/preference` succeeds, which means the backend already
 * accepted the JWT and therefore its `email_verified` claim was true.
 */
function isEmailVerifiedForRoute(userFromContext: unknown): boolean {
  if (typeof window !== 'undefined' && window.location.protocol === 'chrome-extension:') {
    return Boolean(userFromContext);
  }
  return Boolean(auth.currentUser?.emailVerified);
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-[#0d121b]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    try {
      if (typeof window !== 'undefined' && sessionStorage.getItem(REDIRECT_VERIFY_EMAIL_KEY)) {
        sessionStorage.removeItem(REDIRECT_VERIFY_EMAIL_KEY);
        return <Navigate to="/verify-email" state={{ from: location }} replace />;
      }
    } catch {
      // ignore
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isEmailVerifiedForRoute(user)) {
    return <Navigate to="/verify-email" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-[#0d121b]">
        <LoadingSpinner />
      </div>
    );
  }

  if (user) {
    const target = resolvePostLoginPath(location.state);
    clearExtensionReturnPath();
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
};

/**
 * Route wrapper for the `/verify-email` page. Allows authenticated-but-
 * unverified users (which `PublicRoute` would otherwise bounce to `/`)
 * while still redirecting verified users to the dashboard and signed-out
 * users to the login screen.
 */
export const VerifyEmailRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-[#0d121b]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (isEmailVerifiedForRoute(user)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};
